import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Users, Building2, Briefcase, FileText, Calendar, Award, Activity, Plus, Trash2, Edit, Eye, ShieldAlert, Package } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TenantDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [positions, setPositions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'recruiter' });
  const [companyForm, setCompanyForm] = useState({ name: '', industry: '', contact_person: '', contact_email: '', phone: '' });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchOverview = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin-tenant/${slug}/overview`, { headers: getHeaders() });
      setOverview(res.data);
    } catch (e) { console.error('Failed to load tenant:', e); toast.error('Failed to load tenant'); } finally { setLoading(false); }
  }, [slug]);

  const fetchUsers = useCallback(async () => {
    const res = await axios.get(`${API}/admin-tenant/${slug}/users`, { headers: getHeaders() });
    setUsers(res.data);
  }, [slug]);
  const fetchCompanies = useCallback(async () => {
    const res = await axios.get(`${API}/admin-tenant/${slug}/companies`, { headers: getHeaders() });
    setCompanies(res.data);
  }, [slug]);
  const fetchPositions = useCallback(async () => {
    const res = await axios.get(`${API}/admin-tenant/${slug}/positions`, { headers: getHeaders() });
    setPositions(res.data);
  }, [slug]);
  const fetchLogs = useCallback(async () => {
    const res = await axios.get(`${API}/admin-tenant/${slug}/activity-log?limit=100`, { headers: getHeaders() });
    setLogs(res.data);
  }, [slug]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);
  useEffect(() => {
    if (tab === 'users') fetchUsers();
    else if (tab === 'companies') fetchCompanies();
    else if (tab === 'positions') fetchPositions();
    else if (tab === 'activity') fetchLogs();
  }, [tab, fetchUsers, fetchCompanies, fetchPositions, fetchLogs]);

  const createUser = async () => {
    try {
      await axios.post(`${API}/admin-tenant/${slug}/users`, userForm, { headers: getHeaders() });
      toast.success('User created');
      setShowCreateUser(false);
      setUserForm({ name: '', email: '', password: '', role: 'recruiter' });
      fetchUsers();
      fetchOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create user'); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/admin-tenant/${slug}/users/${userId}`, { headers: getHeaders() });
      toast.success('User deleted');
      fetchUsers();
      fetchOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin-tenant/${slug}/users/${userId}`, { role: newRole }, { headers: getHeaders() });
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const createCompany = async () => {
    try {
      await axios.post(`${API}/admin-tenant/${slug}/companies`, companyForm, { headers: getHeaders() });
      toast.success('Company created');
      setShowCreateCompany(false);
      setCompanyForm({ name: '', industry: '', contact_person: '', contact_email: '', phone: '' });
      fetchCompanies();
      fetchOverview();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const deleteCompany = async (companyId) => {
    if (!window.confirm('Delete this company?')) return;
    try {
      await axios.delete(`${API}/admin-tenant/${slug}/companies/${companyId}`, { headers: getHeaders() });
      toast.success('Company deleted');
      fetchCompanies();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const ROLE_COLORS = { admin: 'bg-red-100 text-red-700', team_leader: 'bg-amber-100 text-amber-700', recruiter: 'bg-blue-100 text-blue-700' };

  const handleImpersonate = async () => {
    try {
      const res = await axios.post(`${API}/admin-tenant/${slug}/impersonate`, {}, { headers: getHeaders() });
      // Store impersonation context
      localStorage.setItem('impersonation', JSON.stringify({
        tenantName: res.data.tenant.name,
        tenantSlug: res.data.tenant.slug,
        userName: res.data.user.name
      }));
      // Set tenant session
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      localStorage.setItem('tenant', JSON.stringify(res.data.tenant));
      toast.success(`Impersonating ${res.data.user.name} at ${res.data.tenant.name}`);
      navigate('/');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to impersonate');
    }
  };

  const handleBackfill = async () => {
    if (!window.confirm('Fix orphaned records for this tenant? This stamps tenant_id on any existing data that was saved without it. Safe to run multiple times.')) return;
    try {
      const res = await axios.post(`${API}/admin-tenant/${slug}/backfill-tenant-id`, {}, { headers: getHeaders() });
      const details = res.data?.backfilled || {};
      const summary = Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(', ') || 'nothing needed fixing';
      toast.success(`Backfill done — ${summary}`);
      fetchOverview();
      fetchCompanies();
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill failed');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" /></div>;
  if (!overview) return <div className="text-center py-20 text-slate-500">Tenant not found</div>;

  const { tenant, stats } = overview;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/super-admin/tenants')} data-testid="back-to-tenants">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{tenant.name}</h1>
            <div className="text-slate-500 text-sm">Slug: {tenant.slug} &middot; Plan: <Badge className="bg-indigo-100 text-indigo-700">{tenant.plan}</Badge> &middot; Status: <Badge className={tenant.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{tenant.status}</Badge></div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => navigate(`/super-admin/tenants/${slug}/modules`)}
            className="bg-violet-600 hover:bg-violet-700 text-white"
            data-testid="module-control-btn"
          >
            <Package className="h-4 w-4 mr-2" />
            Module Access
          </Button>
          <Button
            onClick={handleImpersonate}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="impersonate-btn"
          >
            <ShieldAlert className="h-4 w-4 mr-2" />
            Impersonate Tenant
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { label: 'Users', value: stats.users, icon: Users, color: 'text-blue-600' },
          { label: 'Companies', value: stats.companies, icon: Building2, color: 'text-emerald-600' },
          { label: 'Positions', value: stats.positions, icon: Briefcase, color: 'text-violet-600' },
          { label: 'Resumes', value: stats.resumes, icon: FileText, color: 'text-amber-600' },
          { label: 'Interviews', value: stats.interviews, icon: Calendar, color: 'text-pink-600' },
          { label: 'Closures', value: stats.closures, icon: Award, color: 'text-teal-600' },
          { label: 'Joinings', value: stats.joinings, icon: Award, color: 'text-indigo-600' },
          { label: 'Teams', value: stats.teams, icon: Users, color: 'text-orange-600' },
        ].map(s => (
          <Card key={s.label} className="border-slate-200">
            <CardContent className="p-3 text-center">
              <s.icon className={`h-4 w-4 mx-auto ${s.color}`} />
              <p className="text-xl font-bold mt-1">{s.value}</p>
              <p className="text-xs text-slate-500">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="companies" data-testid="tab-companies">Companies</TabsTrigger>
          <TabsTrigger value="positions" data-testid="tab-positions">Positions</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity Log</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <Card className="border-slate-200">
            <CardContent className="pt-6 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-slate-500">Owner:</span> {tenant.owner_name}</div>
                <div><span className="text-slate-500">Email:</span> {tenant.owner_email}</div>
                <div><span className="text-slate-500">Industry:</span> {tenant.industry || 'N/A'}</div>
                <div><span className="text-slate-500">Created:</span> {new Date(tenant.created_at).toLocaleDateString()}</div>
                <div><span className="text-slate-500">Open Positions:</span> {stats.open_positions}</div>
                <div><span className="text-slate-500">Scheduled Interviews:</span> {stats.scheduled_interviews}</div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Users ({users.length})</CardTitle>
              <Button size="sm" onClick={() => setShowCreateUser(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-user-btn">
                <Plus className="h-4 w-4 mr-1" /> Add User
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Email</th><th className="pb-2 pr-4">Role</th><th className="pb-2 pr-4">Team</th><th className="pb-2">Actions</th>
                  </tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id} className="border-b last:border-0" data-testid={`user-row-${u.id}`}>
                        <td className="py-2 pr-4 font-medium">{u.name}</td>
                        <td className="py-2 pr-4 text-slate-500">{u.email}</td>
                        <td className="py-2 pr-4">
                          <Select value={u.role} onValueChange={(v) => updateUserRole(u.id, v)}>
                            <SelectTrigger className="h-7 w-36" data-testid={`role-select-${u.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">admin</SelectItem>
                              <SelectItem value="admin_support">admin_support</SelectItem>
                              <SelectItem value="team_leader">team_leader</SelectItem>
                              <SelectItem value="recruiter">recruiter</SelectItem>
                              <SelectItem value="intern">intern</SelectItem>
                              <SelectItem value="it">it</SelectItem>
                              <SelectItem value="finance">finance</SelectItem>
                              <SelectItem value="marketing">marketing</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-2 pr-4 text-slate-500">{u.team_name || '-'}</td>
                        <td className="py-2">
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteUser(u.id)} data-testid={`delete-user-${u.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="companies">
          <Card className="border-slate-200">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-base">Companies ({companies.length})</CardTitle>
              <Button size="sm" onClick={() => setShowCreateCompany(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-company-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Company
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Name</th><th className="pb-2 pr-4">Industry</th><th className="pb-2 pr-4">Contact</th><th className="pb-2">Actions</th>
                  </tr></thead>
                  <tbody>
                    {companies.map(c => (
                      <tr key={c.id} className="border-b last:border-0" data-testid={`company-row-${c.id}`}>
                        <td className="py-2 pr-4 font-medium">{c.name}</td>
                        <td className="py-2 pr-4 text-slate-500">{c.industry}</td>
                        <td className="py-2 pr-4 text-slate-500">{c.contact_person}</td>
                        <td className="py-2">
                          <Button variant="ghost" size="sm" className="text-red-600" onClick={() => deleteCompany(c.id)} data-testid={`delete-company-${c.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="positions">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Positions ({positions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-slate-500">
                    <th className="pb-2 pr-4">Title</th><th className="pb-2 pr-4">Client</th><th className="pb-2 pr-4">Location</th><th className="pb-2 pr-4">Status</th><th className="pb-2">Openings</th>
                  </tr></thead>
                  <tbody>
                    {positions.map(p => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="py-2 pr-4 font-medium">{p.title}</td>
                        <td className="py-2 pr-4 text-slate-500">{p.client_name}</td>
                        <td className="py-2 pr-4 text-slate-500">{p.location}</td>
                        <td className="py-2 pr-4"><Badge className={p.status === 'open' ? 'bg-emerald-100 text-emerald-700' : p.status === 'closed' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{p.status}</Badge></td>
                        <td className="py-2">{p.num_openings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card className="border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-base">Activity Log</CardTitle></CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <p className="text-slate-500 text-sm py-8 text-center">No activity logs yet</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {logs.map((log, i) => (
                    <div key={`td-${i}`} className="flex items-start gap-3 py-2 border-b last:border-0 text-sm">
                      <Activity className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <span className="font-medium">{log.user_name}</span>
                        <span className="text-slate-500 ml-1">{log.action} {log.entity_type}</span>
                        {log.details && <p className="text-xs text-slate-400">{log.details}</p>}
                      </div>
                      <span className="text-xs text-slate-400 flex-shrink-0">{new Date(log.timestamp).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add User to {tenant.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={userForm.name} onChange={e => setUserForm({ ...userForm, name: e.target.value })} data-testid="new-user-name" /></div>
            <div><Label>Email</Label><Input value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} data-testid="new-user-email" /></div>
            <div><Label>Password</Label><Input type="password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} data-testid="new-user-password" /></div>
            <div>
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="team_leader">Team Leader</SelectItem>
                  <SelectItem value="recruiter">Recruiter</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancel</Button>
            <Button onClick={createUser} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-new-user-btn">Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Company Dialog */}
      <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Company to {tenant.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })} data-testid="new-company-name" /></div>
            <div><Label>Industry</Label><Input value={companyForm.industry} onChange={e => setCompanyForm({ ...companyForm, industry: e.target.value })} /></div>
            <div><Label>Contact Person</Label><Input value={companyForm.contact_person} onChange={e => setCompanyForm({ ...companyForm, contact_person: e.target.value })} /></div>
            <div><Label>Contact Email</Label><Input value={companyForm.contact_email} onChange={e => setCompanyForm({ ...companyForm, contact_email: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={companyForm.phone} onChange={e => setCompanyForm({ ...companyForm, phone: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCompany(false)}>Cancel</Button>
            <Button onClick={createCompany} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="save-new-company-btn">Create Company</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
